import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import { Capacitor } from '@capacitor/core';
import clsx from 'clsx';
import { format } from 'date-fns';

import { ReactComponent as ClockIcon } from '../../../assets/icons/trainings/clock.svg';
import DeleteActionButton from '../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../components/designe/ActionButtons/EditActionButton';
import SortActionButton from '../../../components/designe/ActionButtons/SortActionButton';
import TimerActionButton from '../../../components/designe/ActionButtons/TimerActionButton/TimerActionButton';
import AppButton from '../../../components/designe/buttons/app-button/AppButton';
import { AppDashedBlockButton } from '../../../components/designe/buttons/block-buttons';
import AppPrimaryBlockButton from '../../../components/designe/buttons/block-buttons/AppPrimaryBlockButton';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import AppTimer from '../../../components/reusable/app-timer/AppTimer';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import SortTrainingSetsModal from '../../../components/reusable/sort-training-sets-modal/SortTrainingSetsModal';
import { ViewFieldH2 } from '../../../components/reusable/view-field/ViewField';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { usePullRefreshInvalidateTags } from '../../../hooks/usePullRefreshInvalidateTags';
import { trainingsApi } from '../../../redux/api/trainingsAPI';
import { TRAININGS } from '../../../redux/constants';
import { Paths } from '../../../shared/paths';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { getSetKey } from '../../../utils/training/set-utils';
import EditTrainingModal from '../edit-training-modal/EditTrainingModal';
import { useStyles } from './CurrentTraining.styles';
import CurrentTrainingSetGroup from './current-training-set-group/CurrentTrainingSetGroup';
import CurrentTrainingSkeleton from './current-training-skeleton/CurrentTrainingSkeleton';
import { EditTrainingSetModal } from './edit-training-set-modal/EditTrainingSetModal';
import { useCurrentTrainingPage } from './useCurrentTrainingPage';

export default function CurrentTraining() {
  const { t } = useTranslation();
  const { navigate } = useNavigationStack();

  const theme = useTheme();
  const isNativePlatform = Capacitor.getPlatform() !== 'web';

  const { classes } = useStyles(theme);
  const {
    training,
    isCurrentTrainingLoading,
    isCurrentTrainingError,
    currentTrainingError,
    trainingSetGroups,
    activateTimer,
    activeTimer,
    isSavingTrainingSets,
    onEditTrainingSetSubmit,
    onEditTrainingSetClick,
    onNewTrainingSetClick,
    editedTrainingSetGroup,
    setEditedTrainingSetGroup,
    trainingEditing,
    setTrainingEditing,
    sortSetsModalOpen,
    setSortSetsModalOpen,
    onSaveSetsOrder,
    isSavingSetsOrder,
    orderedTrainingSets,
    deactivateTimer,
    startTimerValue,
    onRunButtonClick,
    showStartTimerButton,
    timerButtomActive,
    onTimerButtonClick,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleteTraining,
    isDeleting,
    refetchCurrentTraining,
  } = useCurrentTrainingPage();
  const shouldShowLoading = !training && isCurrentTrainingLoading;
  const shouldShowContent = !!training && !shouldShowLoading;
  const handleRetry = useCallback(() => {
    void refetchCurrentTraining();
  }, [refetchCurrentTraining]);
  useRegisterPageContainerError({
    errors:
      isCurrentTrainingError && currentTrainingError
        ? [currentTrainingError]
        : [],
    isLoading: shouldShowLoading,
    hasData: Boolean(training),
    onRetry: handleRetry,
  });
  const { onRefresh } = usePullRefreshInvalidateTags(
    [
      {
        apiSlice: trainingsApi,
        tags: training?.id
          ? [
              { type: TRAININGS.TRAININGS, id: training.id },
              { type: TRAININGS.EXERCISES, id: training.id },
            ]
          : [TRAININGS.TRAININGS],
      },
    ],
    {
      onAfterInvalidate: async () => {
        await refetchCurrentTraining();
      },
      hideDurationMs: 0,
    },
  );

  if (shouldShowLoading) {
    return (
      <PageContainer title={t('trainings.currentTraining')} backButton>
        <CurrentTrainingSkeleton />
      </PageContainer>
    );
  }

  if (!shouldShowContent) {
    return null;
  }

  return (
    <PageContainer
      title={t('trainings.currentTraining')}
      backButton
      onRefresh={onRefresh}
      headerContent={
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          paddingRight={1.5}>
          <TimerActionButton
            onClick={() => onTimerButtonClick()}
            active={timerButtomActive}
          />
        </Box>
      }>
      <Box className={classes.headerWrapper}>
        <Box
          display="flex"
          justifyContent="space-between"
          gap={1}
          paddingY={1.5}
          alignItems="flex-start"
          minWidth={0}>
          <Box
            display="flex"
            flexDirection="column"
            gap={1}
            flex={1}
            minWidth={0}
            maxWidth="calc(100% - 70px)">
            <Typography variant="h1" className={classes.trainingTitle}>
              {training?.title}
            </Typography>
            {training?.isGenerated && (
              <Typography variant="primary-wrapped-sm">
                {t('trainings.aiGenerated')}
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            gap={0.5}
            marginTop={0.5}
            alignItems="center"
            flexShrink={0}>
            {training?.permissions?.canEdit && (
              <SortActionButton
                data-onboarding="currentTraining.sortSets"
                onClick={() => setSortSetsModalOpen(true)}
              />
            )}
            {training?.permissions?.canEditInfo && (
              <EditActionButton
                data-onboarding="currentTraining.editTraining"
                onClick={() => setTrainingEditing(true)}
              />
            )}
            {training?.permissions?.canDelete && (
              <DeleteActionButton
                data-onboarding="currentTraining.deleteTraining"
                onClick={() => setShowDeleteConfirmation(true)}
              />
            )}
          </Box>
        </Box>
        {!!training?.startDate && (
          <ViewFieldH2
            value={format(new Date(training?.startDate), 'dd.MM.yyyy HH:mm')}
            label={t('trainings.startDate')}
            startAdornment={
              <Box className={classes.clockIconWrapper}>
                <ClockIcon />
              </Box>
            }
          />
        )}
      </Box>

      <Box className={classes.trainingSetGroupsWrapper}>
        {trainingSetGroups &&
          Object.values(trainingSetGroups)?.map(
            (trainingSetGroup: ITrainingSet[]) => (
              <CurrentTrainingSetGroup
                key={getSetKey(trainingSetGroup[0])}
                trainingSetGroup={trainingSetGroup}
                trainingId={training.id}
                activateTimer={activateTimer}
                onEditTrainingSetClick={onEditTrainingSetClick}
              />
            ),
          )}
      </Box>

      <Box
        marginTop={2}
        display="flex"
        gap={2}
        flexDirection="column"
        marginBottom={2}>
        <AppDashedBlockButton
          data-onboarding="currentTraining.addExercise"
          fullWidth
          onClick={() => onNewTrainingSetClick()}>
          <Box display="flex" gap={1} alignItems="center">
            <AddIcon />
            <Typography variant="h3">{t('trainings.addExercise')}</Typography>
          </Box>
        </AppDashedBlockButton>

        <AppPrimaryBlockButton
          data-onboarding="currentTraining.finishTraining"
          fullWidth
          onClick={() => {
            if (training?.id) {
              navigate(Paths.trainings.toFinishing(training.id));
            }
          }}>
          <Box display="flex" gap={1} alignItems="center">
            <CheckIcon />
            <Typography variant="h3">
              {t('trainings.finishTraining')}
            </Typography>
          </Box>
        </AppPrimaryBlockButton>
      </Box>

      <AppDialog
        open={!!activeTimer}
        onClose={() => deactivateTimer()}
        title="">
        {activeTimer?.type === 'time' && (
          <AppTimer
            id={activeTimer.timerId}
            startTime={activeTimer.time}
            autoStart={activeTimer.autostart}
            startValue={activeTimer.startValue}
            onClose={() => deactivateTimer()}
            onFinish={activeTimer.onFinish}
          />
        )}
        {/* {activeTimer?.type === 'time' && (
          <CurrentTrainingStopwatch
            id={activeTimer.timerId}
            startTime={activeTimer.time}
            autoStart={activeTimer.autostart}
            label=""
            onClose={() => activateTimer(null)}
            onFinish={activeTimer.onFinish}
          />
        )} */}
      </AppDialog>
      {Number.isInteger(startTimerValue) && (
        <AppButton
          variant="text"
          className={clsx(
            classes.startTimerButton,
            showStartTimerButton && classes.startTimerButtonVisible,
            isNativePlatform && classes.startTimerButtonNativeFixed,
          )}
          onClick={() => onRunButtonClick()}>
          <PlayArrowIcon />
        </AppButton>
      )}

      <EditTrainingSetModal
        trainingSetGroup={editedTrainingSetGroup}
        setTrainingSetGroup={setEditedTrainingSetGroup}
        onSubmit={onEditTrainingSetSubmit}
        isSavingTrainingSets={isSavingTrainingSets}
        trainingOwnerId={training?.ownerId}
      />

      <SortTrainingSetsModal
        open={sortSetsModalOpen}
        onClose={() => setSortSetsModalOpen(false)}
        trainingSets={orderedTrainingSets}
        onSave={onSaveSetsOrder}
        isSaving={isSavingSetsOrder}
      />

      <EditTrainingModal
        isEditTraining={trainingEditing && !!training?.permissions?.canEditInfo}
        editedTraining={training}
        onCancel={() => setTrainingEditing(false)}
      />

      <ConfirmDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title={t('common.delete')}
        onSubmit={() => deleteTraining()}
        isLoading={isDeleting}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </PageContainer>
  );
}
